import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { CssBaseline, CircularProgress, Box, Container } from '@mui/material';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import './i18n';
import { AppThemeProvider } from './context/ThemeContext';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import ToolsGrid from './components/ToolsGrid';
import FeaturesSection from './components/FeaturesSection';
import HowItWorks from './components/HowItWorks';
import Footer from './components/Footer';
import ToolPageLayout from './components/ToolPageLayout';
import StructuredData from './components/StructuredData';
import FaqSection from './components/FaqSection';
import AdSensePlaceholder from './components/AdSensePlaceholder';
import Markdown from './components/Markdown';
import toolBodies from './content/toolBodies.json';

// PdfMerger is the homepage tool and pulls in pdfjs-dist (~150-200 KB gzipped).
// Lazy-loading it keeps pdf.js out of the entry bundle so first paint is fast for
// every visitor — including those who never upload a file.
const PdfMerger      = lazy(() => import('./components/PdfMerger'));
const PdfImageMerger = lazy(() => import('./components/PdfImageMerger'));
const PdfSplitter   = lazy(() => import('./components/PdfSplitter'));
const PdfCompressor = lazy(() => import('./components/PdfCompressor'));
const PdfToImages   = lazy(() => import('./components/PdfToImages'));
const PdfRotator    = lazy(() => import('./components/PdfRotator'));
const PageNumberer  = lazy(() => import('./components/PageNumberer'));
const PdfProtect    = lazy(() => import('./components/PdfProtect'));
const PdfUnlock     = lazy(() => import('./components/PdfUnlock'));
const ImagesToPdf   = lazy(() => import('./components/ImagesToPdf'));
const CreatePdf     = lazy(() => import('./components/CreatePdf'));
const PdfWatermark  = lazy(() => import('./components/PdfWatermark'));
const PdfExtract    = lazy(() => import('./components/PdfExtract'));
const PdfFlatten    = lazy(() => import('./components/PdfFlatten'));
const PdfResize     = lazy(() => import('./components/PdfResize'));
const PdfSign       = lazy(() => import('./components/PdfSign'));
const OfficeToPdf   = lazy(() => import('./components/OfficeToPdf'));
const PdfToWord     = lazy(() => import('./components/PdfToWord'));
const PdfToPpt      = lazy(() => import('./components/PdfToPpt'));
const PdfToPdfA     = lazy(() => import('./components/PdfToPdfA'));
const OcrPdf        = lazy(() => import('./components/OcrPdf'));
const PdfCrop       = lazy(() => import('./components/PdfCrop'));
const PdfRepair     = lazy(() => import('./components/PdfRepair'));
const PdfMetadata   = lazy(() => import('./components/PdfMetadata'));
const PdfOrganize   = lazy(() => import('./components/PdfOrganize'));
const PdfEdit       = lazy(() => import('./components/PdfEdit'));
const ApiDocs       = lazy(() => import('./components/ApiDocs'));
const Privacy       = lazy(() => import('./components/Privacy'));
const Terms         = lazy(() => import('./components/Terms'));
const ContentPage   = lazy(() => import('./components/ContentPage'));
const BlogIndex     = lazy(() => import('./components/BlogIndex'));
const BlogPost      = lazy(() => import('./components/BlogPost'));
const GlossaryIndex = lazy(() => import('./components/GlossaryIndex'));
const GlossaryTerm  = lazy(() => import('./components/GlossaryTerm'));
const NotFound      = lazy(() => import('./components/NotFound'));

function LoadingFallback() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );
}

function HomePage() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{t('seo.title')}</title>
        <meta name="description" content={t('seo.description')} />
      </Helmet>
      <StructuredData name={t('seo.title')} description={t('seo.description')} path="" />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box component="main" sx={{ flex: 1 }}>
          <HeroSection />
          <FeaturesSection />
          <HowItWorks />
          <Suspense fallback={<LoadingFallback />}>
            <PdfMerger />
          </Suspense>
          <ToolsGrid />
          <Container maxWidth="md">
            <AdSensePlaceholder slot="in-article" format="in-article" />
          </Container>
          {toolBodies['/'] && (
            <Box component="section" sx={{ background: 'linear-gradient(180deg, transparent, rgba(99,102,241,0.03))' }}>
              <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
                <Markdown headingShift={1}>{toolBodies['/']}</Markdown>
              </Container>
            </Box>
          )}
          <FaqSection />
          <Container maxWidth="md">
            <AdSensePlaceholder slot="multiplex" format="multiplex" sx={{ mb: 2 }} />
          </Container>
        </Box>
        <Footer />
      </Box>
    </>
  );
}

function ToolRoute({ Component, ads = true, props }) {
  return (
    <ToolPageLayout ads={ads}>
      <Suspense fallback={<LoadingFallback />}>
        <Component {...props} />
      </Suspense>
    </ToolPageLayout>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AppThemeProvider>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="/"             element={<HomePage />} />
            <Route path="/merge-pdf-images" element={<ToolRoute Component={PdfImageMerger} />} />
            <Route path="/split-pdf"    element={<ToolRoute Component={PdfSplitter} />} />
            <Route path="/compress-pdf" element={<ToolRoute Component={PdfCompressor} />} />
            <Route path="/pdf-to-images" element={<ToolRoute Component={PdfToImages} />} />
            <Route path="/rotate-pdf"   element={<ToolRoute Component={PdfRotator} />} />
            <Route path="/number-pages" element={<ToolRoute Component={PageNumberer} />} />
            <Route path="/protect-pdf"  element={<ToolRoute Component={PdfProtect} />} />
            <Route path="/unlock-pdf"   element={<ToolRoute Component={PdfUnlock} />} />
            <Route path="/images-to-pdf" element={<ToolRoute Component={ImagesToPdf} />} />
            <Route path="/create-pdf"   element={<ToolRoute Component={CreatePdf} />} />
            <Route path="/watermark-pdf" element={<ToolRoute Component={PdfWatermark} />} />
            <Route path="/extract-pdf"  element={<ToolRoute Component={PdfExtract} />} />
            <Route path="/flatten-pdf"  element={<ToolRoute Component={PdfFlatten} />} />
            <Route path="/resize-pdf"   element={<ToolRoute Component={PdfResize} />} />
            <Route path="/sign-pdf"     element={<ToolRoute Component={PdfSign} />} />
            <Route path="/office-to-pdf" element={<ToolRoute Component={OfficeToPdf} />} />
            <Route path="/word-to-pdf"  element={<ToolRoute Component={OfficeToPdf} props={{ variant: 'word' }} />} />
            <Route path="/excel-to-pdf" element={<ToolRoute Component={OfficeToPdf} props={{ variant: 'excel' }} />} />
            <Route path="/powerpoint-to-pdf" element={<ToolRoute Component={OfficeToPdf} props={{ variant: 'ppt' }} />} />
            <Route path="/html-to-pdf"  element={<ToolRoute Component={OfficeToPdf} props={{ variant: 'html' }} />} />
            <Route path="/png-to-pdf"   element={<ToolRoute Component={ImagesToPdf} props={{ variant: 'png' }} />} />
            <Route path="/pdf-to-jpg"   element={<ToolRoute Component={PdfToImages} props={{ variant: 'jpg' }} />} />
            <Route path="/pdf-to-word"  element={<ToolRoute Component={PdfToWord} />} />
            <Route path="/pdf-to-ppt"   element={<ToolRoute Component={PdfToPpt} />} />
            <Route path="/pdf-to-pdfa"  element={<ToolRoute Component={PdfToPdfA} />} />
            <Route path="/ocr-pdf"      element={<ToolRoute Component={OcrPdf} />} />
            <Route path="/crop-pdf"     element={<ToolRoute Component={PdfCrop} />} />
            <Route path="/repair-pdf"   element={<ToolRoute Component={PdfRepair} />} />
            <Route path="/edit-metadata" element={<ToolRoute Component={PdfMetadata} />} />
            <Route path="/organize-pdf" element={<ToolRoute Component={PdfOrganize} />} />
            <Route path="/delete-pages" element={<ToolRoute Component={PdfOrganize} props={{ variant: 'delete' }} />} />
            <Route path="/edit-pdf"     element={<ToolRoute Component={PdfEdit} />} />
            {/* No ads on legal/utility pages: they carry little publisher content of their own. */}
            <Route path="/api-docs"     element={<ToolRoute Component={ApiDocs} ads={false} />} />
            <Route path="/privacy"      element={<ToolRoute Component={Privacy} ads={false} />} />
            <Route path="/terms"        element={<ToolRoute Component={Terms} ads={false} />} />
            <Route path="/about"        element={<ToolRoute Component={ContentPage} ads={false} props={{ page: 'about', path: '/about' }} />} />
            <Route path="/contact"      element={<ToolRoute Component={ContentPage} ads={false} props={{ page: 'contact', path: '/contact' }} />} />
            <Route path="/help"         element={<ToolRoute Component={ContentPage} props={{ page: 'help', path: '/help' }} />} />
            <Route path="/blog"         element={<ToolRoute Component={BlogIndex} />} />
            <Route path="/blog/:slug"   element={<ToolRoute Component={BlogPost} />} />
            <Route path="/glossary"     element={<ToolRoute Component={GlossaryIndex} />} />
            <Route path="/glossary/:slug" element={<ToolRoute Component={GlossaryTerm} />} />
            <Route path="*"             element={<ToolRoute Component={NotFound} ads={false} />} />
          </Routes>
        </BrowserRouter>
      </AppThemeProvider>
    </HelmetProvider>
  );
}
